import React, {useState, useContext} from 'react';
import Typography from '@mui/material/Typography';
import MFACompound from '../../../common/components/Inputs/MFACompound';
import OTPInputField from '../../../common/components/Inputs/OTPInputField';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ApiClient from '../../../../services/ApiClient';
import NewUUID from '../../../../utils/randy/NewUUID';
import { parseClaimsFromHeader } from '../../../../utils/auth/ClaimsParser';
import WithActions from "../../../common/components/Wrappers/WithActions";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import DisabledFormState from "../../../common/components/Wrappers/DisabledFormState";
import { ThemeContext } from 'styled-components';

const SuccessMessage = ({ children }) => (
    <Alert
        severity="success"
        icon={<CheckCircleIcon />}
        sx={{ mt: 2 }}
    >
        {children}
    </Alert>
);

const SetupMFAForm = ({
    formData,
    onChangeAuthForm,
    handleLogin,
    onStepTransition,
    onLoginSuccess,
    onLoginError,
    isLoading,
    setIsLoading,
    csrfToken,
    email,
    password,
    error,
    showQR,
    disabled = false,
    isVisible = true,
    isActive = true
}) => {
    const [localError, setLocalError] = useState(null);
    const [mfaVerificationCode, setMfaVerificationCode] = useState('');
    const theme = useContext(ThemeContext);

    // Safety check for formData - must be after hooks
    if (!formData || !isVisible || !formData.mfaData) {
        return null;
    }

    if (disabled) {
        return <DisabledFormState message="MFA Configured" tooltip="MFA Configured" />
    }

    const handleMFASetup = async (e) => {
        e.preventDefault();
        if (isLoading) {
            return;
        }

        // Validate form and show errors if invalid
        if (mfaVerificationCode.length !== 6) {
            setLocalError("Please enter a complete 6-digit MFA setup code");
            return;
        }

        setIsLoading(true);
        setLocalError(null);

        try {
            const response = await ApiClient.post('/api/auth/mfa/register', {
                email: email,
                mfa_user_code: mfaVerificationCode,
                mfa_session_id: formData.mfaData.mfa_session_id,
                ext_user_id: formData.mfaData.ext_user_id,
            }, {
                headers: {
                    accept: "application/json",
                    request_id: NewUUID(),
                    "X-CSRF-Token": csrfToken.toString()
                }
            });

            if (response.data.error) {
                onLoginError("Invalid MFA setup code. Please try again.");
                return;
            }

            // MFA setup successful, now attempt login to get verification session
            await handleLogin(
                email,
                password,
                {
                    onMfaSetup: (mfaData) => {
                        // This shouldn't happen after MFA setup
                        onLoginError("Unexpected MFA setup required after setup completion.");
                        setIsLoading(false);
                    },
                    onMfaVerify: (mfaData) => {
                        // Store MFA verification data for re-verify step
                        formData.mfaSetupComplete = true;
                        formData.mfaVerifyData = mfaData;
                        onStepTransition('verifyMFA', null, formData);
                        setIsLoading(false);
                    },
                    onError: (error) => {
                        onLoginError(error);
                        setIsLoading(false);
                    },
                    onSuccess: (claims) => {
                        onLoginSuccess({ ...claims, email });
                        setIsLoading(false);
                    }
                }
            );
        } catch (err) {
            console.error('MFA setup error:', err);
            onLoginError("Invalid MFA setup code. Please try again.");
            setIsLoading(false);
        }
    };

    const handleMFAReverify = async (e) => {
        e.preventDefault();
        if (isLoading) {
            return;
        }

        // Validate form and show errors if invalid
        if (formData.mfaReverifyCode.length !== 6) {
            setLocalError("Please enter a complete 6-digit MFA verification code");
            return;
        }

        setIsLoading(true);
        setLocalError(null);

        try {
            const response = await ApiClient.post('/api/auth/mfa/verify', {
                email: email,
                mfa_user_code: formData.mfaReverifyCode,
                mfa_session_id: formData.mfaVerifyData.mfa_session_id,
                ext_user_id: formData.mfaVerifyData.ext_user_id,
            }, {
                headers: {
                    accept: "application/json",
                    request_id: NewUUID(),
                    "X-CSRF-Token": csrfToken.toString()
                }
            });

            if (response.data.error) {
                onLoginError("Invalid MFA code. Please try again.");
                setIsLoading(false);
                return;
            }

            const claims = parseClaimsFromHeader(response);
            if (claims && claims.user_sub) {
                onLoginSuccess({ ...claims, email });
                setIsLoading(false);
            } else if (response.data.claims) {
                onLoginSuccess({ ...response.data.claims, email });
                setIsLoading(false);
            } else {
                // Fallback to UserID cookie
                const userID = document.cookie.split(';').find(cookie => cookie.trim().startsWith('UserID='));
                if (userID) {
                    const userIdValue = userID.split('=')[1];
                    const claims = { user_sub: userIdValue, email };
                    onLoginSuccess(claims);
                    setIsLoading(false);
                } else {
                    onLoginError("Authentication failed. Please try again.");
                    setIsLoading(false);
                }
            }
        } catch (err) {
            console.error('MFA re-verify error:', err);
            onLoginError("Invalid MFA code. Please try again.");
            setIsLoading(false);
        }
    };

    // Auto-submit removed - now handled by external button

    // Auto-submit removed - now handled by external button

    return (
        <Box
            component="form"
            onSubmit={handleMFASetup}
            sx={{
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                margin: '8px 0',
                transition: 'all 0.3s ease'
            }}
        >
            <Box sx={{ mt: -1 }}>
                <WithActions
                    forceSpacing
                    inputType="text"
                >
                    <Typography
                        component="div"
                        sx={{
                            color: theme.colors.text,
                            textAlign: 'left',
                            width: '100%',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            margin: 0,
                            padding: 0,
                            lineHeight: '40px',
                            height: '40px',
                            display: 'block',
                        }}
                    >
                        Configure MFA
                    </Typography>
                </WithActions>
            </Box>

            {/* Original MFA Setup - Always visible, greyed out after setup */}
            <MFACompound
                qrCodeUrl={formData.mfaData.qr_code_url}
                qrCodeSecret={formData.mfaData.secret_code}
                verificationCode={mfaVerificationCode}
                onChangeAuthForm={(e) => setMfaVerificationCode(e.target.value)}
                showQR={showQR}
                showOTP={true}
                description="Enter the 6-digit code from your authenticator app"
                disabled={disabled}
                sx={{ mb: 2 }}
            />
            {(!disabled) && (
                <WithActions forceSpacing={true} isButton={true}>
                <ButtonPrimary
                    type="submit"
                    disabled={isLoading}
                >
                    Confirm
                </ButtonPrimary>
                </WithActions>
            )}
        </Box>
    );
};

export default SetupMFAForm;
